import React, { useMemo } from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Statistics from '../../../model/API/Statistics/Statistics';

interface SummaryItem {
  label: string;
  value: string | number;
  unit: string;
}

const GeneralStatisticsTable: React.FC<GeneralStatisticsTableProps> = ({
  general,
  loading,
  error,
  unit,
  flowUnit,
}: GeneralStatisticsTableProps) => {
  const { t } = useTranslation();

  const summaryItems = useMemo<SummaryItem[]>(
    () => [
      {
        label: t('statistics_quantity_minimum'),
        value: formatNumber(general?.min),
        unit: flowUnit,
      },
      {
        label: t('statistics_quantity_maximum'),
        value: formatNumber(general?.max),
        unit: flowUnit,
      },
      {
        label: t('statistics_quantity_average'),
        value: formatNumber(general?.avg),
        unit: flowUnit,
      },
      {
        label: t('statistics_quantity_total'),
        value: formatNumber(general?.total),
        unit,
      },
      {
        label: t('statistics_quantity_hours'),
        value: general?.hours ?? 0,
        unit: 'h',
      },
      {
        label: t('statistics_quantity_counters'),
        value: general?.counters ?? 0,
        unit: '',
      },
    ],
    [flowUnit, general, t, unit]
  );

  if (loading) {
    return (
      <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Typography variant="h6">{t('statistics_header')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('loading')}...
        </Typography>
      </Paper>
    );
  }

  if (!general) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: 1,
          borderStyle: 'dashed',
          borderColor: 'divider',
          borderRadius: 2,
          backgroundColor: 'grey.50',
          px: 3,
          py: 5,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6">{t('statistics_header')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {error || t('no_data_found')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ px: 3 }}>
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        {summaryItems.map((item: SummaryItem) => (
          <Grid item xs={6} md={2} key={item.label}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'text.secondary',
                }}
              >
                {item.label}
              </Typography>
              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {item.value}
                </Typography>
                {item.unit && (
                  <Typography variant="body2" color="text.secondary" sx={{ pb: 0.25 }}>
                    {item.unit}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

const formatNumber = (value: number | null | undefined): string => {
  return value === null || value === undefined ? '0.00' : value.toFixed(2);
};

interface GeneralStatisticsTableProps {
  general: Statistics | null;
  loading: boolean;
  error: string | null;
  unit: string;
  flowUnit: string;
}

export default GeneralStatisticsTable;
